import React, { useCallback, useMemo, useEffect } from 'react';
import ENVIRONMENT from '../../../../environments';
import CustomerMainLayout from '../CustomerMainLayout';

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// Translate the legacy URL the user landed on into the equivalent path inside
// the migrated portal. The iframe owns sub-routing via React Router v6.
const legacyPathToMigratedPath = (pathname, params) => {
  if (pathname === '/new-style-guidelines') return '/style-guidelines/new';
  if (params?.id) {
    if (pathname.startsWith('/edit-style-guidelines/')) {
      return `/style-guidelines/edit/${params.id}`;
    }
    if (pathname.startsWith('/style-guidelines/')) {
      return `/style-guidelines/${params.id}`;
    }
  }
  return '/style-guidelines';
};

// Translate a migrated portal path back into the legacy URL bar shape.
const migratedPathToLegacyPath = (path) => {
  if (path === '/style-guidelines' || path === '/style-guidelines/') return '/style-guidelines';
  if (path === '/style-guidelines/new') return '/new-style-guidelines';
  const editMatch = path.match(/^\/style-guidelines\/edit\/([^/?#]+)/);
  if (editMatch) return `/edit-style-guidelines/${editMatch[1]}`;
  const viewMatch = path.match(/^\/style-guidelines\/([^/?#]+)/);
  if (viewMatch) return `/style-guidelines/${viewMatch[1]}`;
  return '/style-guidelines';
};

const MigratedStyleGuidelines = ({ location, match }) => {
  const iframeRef = React.useRef(null);
  const migratedPortalBaseUrl = (
    ENVIRONMENT.MIGRATED_PORTAL_URL ||
    process.env.REACT_APP_MIGRATED_PORTAL_URL ||
    'http://localhost:5173'
  ).replace(/\/$/, '');

  const initialMigratedPath = useMemo(
    () => legacyPathToMigratedPath(location?.pathname || '/style-guidelines', match?.params),
    // Compute once on mount; subsequent route changes inside the iframe are
    // handled via postMessage so we don't reload the iframe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const iframeSrc = `${migratedPortalBaseUrl}${initialMigratedPath}${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, payload } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      } else if (type === 'ALL3D_NAVIGATE') {
        window.location.href = event.data.url || '';
      } else if (type === 'ALL3D_NAVIGATE_REPLACE') {
        const migratedPath = (payload?.url || '').split('?')[0];
        const search = (payload?.url || '').includes('?')
          ? '?' + payload.url.split('?')[1]
          : '';
        if (!migratedPath.startsWith('/style-guidelines')) return;
        const legacyPath = migratedPathToLegacyPath(migratedPath);
        const newUrl = legacyPath + search;
        if (newUrl !== window.location.pathname + window.location.search) {
          window.history.replaceState(null, '', newUrl);
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin]);

  return (
    <CustomerMainLayout selected="40" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="Style Guidelines"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>
    </CustomerMainLayout>
  );
};

export default MigratedStyleGuidelines;
